import React, { useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// layout components
import Preloader from "components/Preloader";
import ErrorBoundary from "engine/ErrorBoundary";

// antd component
import { List, Empty, Tooltip } from "antd";

// assets
import CustomIcons from "components/CustomIcons";

// utils
import history from "utils/history";
import { getObjId, sortByKey } from "utils/helpers/helper.general";
import { getAttendancesOnDate } from "utils/helpers/helper.generateData";
import _local from "utils/localStorageUtils";
import { convertTimeToString } from "utils/moment/moment.utils";

function AgendaList({
  dataSourse,
  selectedDate,
  timeZone,
  appointments,
  // states
  loading,
  // default props
  className,
  style,
}) {
  const mainClassNames = classNames("agenda-wrapper", className);
  const { t } = useTranslation();

  const localUser = _local.getLocalUser();

  const clickHandle = useCallback(
    itemID => {
      history.push({
        pathname: `/agenda/${itemID}`,
        search: "",
        hash: "",
        state: {
          from: "agenda",
          needRefresh: false,
          onDate: selectedDate || false,
          timeZone: timeZone || false,
          goBack: "/agenda",
          goBackTitle: t("layouts.navigation.agenda"),
        },
      });
    },
    [localUser?.role, selectedDate, history],
  );

  const renderItem = useCallback(
    item => {
      const courseClasses =
        item?.classes?.length > 0 &&
        item?.classes?.map(classInfo => classInfo?.title)?.join(", ");
      return (
        <List.Item
          key={item?._id + "-course-list"}
          className="list-items"
          onClick={() => !item?.appointmentDate && clickHandle(item?._id)}
        >
          <List.Item.Meta
            title=""
            className="list-items-meta"
            description={
              <>
                {item?.course_time_start && (
                  <span className="list-items-meta-start-time">
                    {convertTimeToString(item?.course_time_start)}
                  </span>
                )}

                <div className="list-item-meta-wrapper">
                  <div className="list-item-meta-subject-wrapper">
                    <span className="list-items-meta-subject-name">
                      {item?.subject?.name} {!!item?.course_code ? ` • ${item?.course_code}`:""}
                    </span>

                    <div className="list-items-meta-wrapper-marks">
                      {!!item?.hasAtt && (
                        <Tooltip
                          placement="top"
                          trigger="hover"
                          color="#28a745"
                          title={t("attendances.controlScreenTitle")}
                          className="mark-tooltip"
                          getPopupContainer={trigger => trigger.parentElement}
                        >
                          <span className="list-items-meta-mark attendance-mark">
                            <CustomIcons type="registred" />
                          </span>
                        </Tooltip>
                      )}
                      {!!courseClasses && localUser?.role == "teacher" && (
                        <span className="list-items-meta-teacher">
                          {courseClasses}
                        </span>
                      )}
                      {!item?.appointmentDate && (
                        <span className="list-items-meta-class-room">
                          {item?.class_room ? item?.class_room : "--"}
                        </span>
                      )}
                      {!!item?.appointmentDate && (
                        <span className="list-items-meta-mark appointment-mark">
                          <CustomIcons type="calendar" />
                        </span>
                      )}
                    </div>
                  </div>

                  {/*

              --- For Future sprints

              {item?.homework &&
              item?.lesson &&
              activeSubject &&
              activeSubjectId === item?._id && (
                <div className="list-items-meta-wrapper-extra-data">
                  <div className="extra-data-homework">
                    <span className="extra-data-homework-title">
                      Devoirs
                    </span>

                    <span className="extra-data-homework-item">
                      {item?.homework}
                    </span>
                  </div>
                  <div className="extra-data-lesson">
                    <span className="extra-data-lesson-title">
                      Leçons
                    </span>

                    <span className="extra-data-lesson-item">
                      {item?.lesson}
                    </span>
                  </div>
                </div>
              )} */}
                </div>
              </>
            }
          />
        </List.Item>
      );
    },
    [history, localUser],
  );

  let dataSourseNew =
    dataSourse?.map(el => {
      const currentAttendanceArr = getAttendancesOnDate(el, selectedDate);
      const att = currentAttendanceArr?.[0];
      return {
        ...el,
        title: `<span className="time">${convertTimeToString(
          el?.course_time_start,
        )}</span> <span className="cours-name">${el?.subject?.name}</span>`,
        hasAtt: getObjId(att?.course) == getObjId(el),
      };
    }) || [];

  // Add appointments to the list and adapt it
  if (!!appointments && Array.isArray(appointments)) {
    dataSourseNew = [...dataSourseNew, ...appointments];
  }

  dataSourseNew = dataSourseNew
    ?.map(el => {
      if (!!el?.course_time_start) {
        return el;
      }
      if (!!el?.adminAnswer) {
        return {
          ...el,
          course_time_start: el?.adminAnswer?.slot?.timeFrom,
          subject: {
            name: el?.title,
          },
          students: [el?.student],
          title: `<span className="time">${convertTimeToString(
            el?.adminAnswer?.slot?.timeFrom,
          )}</span> <span className="cours-name">${el?.title}</span>`,
        };
      } else {
        return el;
      }
    })
    .sort(sortByKey("title"));

  return (
    <div className={mainClassNames} style={style}>
      <ErrorBoundary>
        <List
          header=""
          className="agenda-wrapper__list"
          dataSource={dataSourseNew}
          renderItem={renderItem}
          loading={{
            spinning: loading,
            indicator: <Preloader size="small" />,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("errors.noData")}
              />
            ),
          }}
        />
      </ErrorBoundary>
    </div>
  );
}

AgendaList.defaultProps = {
  showAll: false,
  loading: false,
};
AgendaList.propTypes = {
  dataSourse: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.array.isRequired,
  ]),
  loading: PropTypes.bool,
  showAll: PropTypes.bool,
};

export default AgendaList;
